import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  EnvelopeSimple, MagnifyingGlass, ArrowRight, Archive, Trash, CheckCircle,
  PaperPlaneTilt, ChatCircle
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  new: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  read: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  replied: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  archived: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ContactSubmissionsPage() {
  const { apiCall } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', search: '' });
  const [active, setActive] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  useEffect(() => { load(); loadStats(); /* eslint-disable-next-line */ }, [filters.status]);

  async function load() {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.status !== 'all') qs.set('status', filters.status);
      if (filters.search) qs.set('search', filters.search);
      const res = await apiCall('get', `/contact${qs.toString() ? `?${qs.toString()}` : ''}`);
      setSubmissions(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function loadStats() {
    try {
      const res = await apiCall('get', '/contact/stats');
      setStats(res.data);
    } catch (e) {
      // Stats are informational — log but don't surface a toast on every poll.
      console.warn('contact loadStats failed:', e);
    }
  }

  async function openSubmission(s) {
    setActive(s);
    if (s.status === 'new') {
      try {
        await apiCall('patch', `/contact/${s.id}`, { status: 'read' });
        load(); loadStats();
      } catch (e) {
        // Auto-mark-as-read is opportunistic; opening the submission still works
        // even if the status patch fails (e.g. transient network error).
        console.warn('auto mark-as-read failed:', e);
      }
    }
  }

  async function updateStatus(id, status) {
    try {
      await apiCall('patch', `/contact/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load(); loadStats();
      if (active && active.id === id) setActive(a => ({ ...a, status }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  }

  async function deleteSubmission(id) {
    try {
      await apiCall('delete', `/contact/${id}`);
      toast.success('Submission deleted');
      setConfirmDel(null);
      if (active && active.id === id) setActive(null);
      load(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="contact-submissions-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <EnvelopeSimple size={28} className="text-primary" weight="duotone" />
          Contact Submissions
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Leads from the landing page. We also email these to info@roofmate.app.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total" value={stats?.total ?? '—'} testid="contact-stat-total" />
        <StatCard label="New" value={stats?.new ?? '—'} tone="emerald" testid="contact-stat-new" />
        <StatCard label="Replied" value={stats?.replied ?? '—'} tone="purple" testid="contact-stat-replied" />
        <StatCard label="Archived" value={stats?.archived ?? '—'} tone="slate" testid="contact-stat-archived" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name, email, subject, message…"
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            className="pl-9"
            data-testid="contact-search-input"
          />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-[170px]" data-testid="contact-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="replied">Replied</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading…</div>
      ) : submissions.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <EnvelopeSimple size={48} weight="duotone" className="mx-auto mb-3 text-muted-foreground/40" />
            No contact submissions yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {submissions.map(s => (
            <Card
              key={s.id}
              className={`cursor-pointer hover:border-primary/40 transition-colors ${s.status === 'new' ? 'border-emerald-500/30' : ''}`}
              onClick={() => openSubmission(s)}
              data-testid={`contact-row-${s.id}`}
            >
              <CardContent className="py-4 px-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[s.status] || ''}`}>{s.status}</Badge>
                    <span className="text-xs text-muted-foreground">{fmtDate(s.created_at)}</span>
                  </div>
                  <div className="font-medium truncate">{s.subject}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    <span className="font-medium">{s.name}</span> · {s.email}
                  </div>
                  <div className="text-xs text-muted-foreground/80 truncate mt-1 max-w-2xl">{s.message?.slice(0, 140)}</div>
                </div>
                <ArrowRight size={16} className="text-muted-foreground shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      {active && (
        <Dialog open onOpenChange={(v) => !v && setActive(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-detail-dialog">
            <DialogHeader>
              <div className="flex items-start justify-between gap-3 flex-wrap pr-6">
                <div className="min-w-0">
                  <DialogTitle className="text-lg break-words">{active.subject}</DialogTitle>
                  <DialogDescription className="mt-1">
                    {fmtDate(active.created_at)}
                  </DialogDescription>
                </div>
                <Badge variant="outline" className={STATUS_COLORS[active.status]}>{active.status}</Badge>
              </div>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-muted/40 border">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Name</div>
                  <div className="text-sm font-medium">{active.name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Email</div>
                  <a href={`mailto:${active.email}?subject=Re:%20${encodeURIComponent(active.subject || '')}`} className="text-sm text-primary hover:underline break-all" data-testid="contact-detail-email-link">
                    {active.email}
                  </a>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Message</div>
                <div className="p-3 rounded-md border bg-card whitespace-pre-wrap text-sm leading-relaxed">
                  {active.message}
                </div>
              </div>

              {(active.ip_address || active.user_agent) && (
                <div className="text-[11px] text-muted-foreground/70 space-y-0.5">
                  {active.ip_address && <div>IP: {active.ip_address}</div>}
                  {active.user_agent && <div className="truncate">UA: {active.user_agent}</div>}
                </div>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="ghost"
                onClick={() => setConfirmDel(active)}
                className="gap-1 text-destructive hover:text-destructive"
                data-testid="contact-delete-btn"
              >
                <Trash size={14} /> Delete
              </Button>
              <div className="flex flex-wrap gap-2 sm:ml-auto">
                {active.status !== 'archived' && (
                  <Button variant="outline" onClick={() => updateStatus(active.id, 'archived')} className="gap-1" data-testid="contact-archive-btn">
                    <Archive size={14} /> Archive
                  </Button>
                )}
                {active.status !== 'replied' && (
                  <Button variant="outline" onClick={() => updateStatus(active.id, 'replied')} className="gap-1" data-testid="contact-mark-replied-btn">
                    <CheckCircle size={14} /> Mark replied
                  </Button>
                )}
                <a
                  href={`mailto:${active.email}?subject=Re:%20${encodeURIComponent(active.subject || '')}`}
                  data-testid="contact-reply-btn"
                >
                  <Button className="gap-1">
                    <PaperPlaneTilt size={14} weight="fill" /> Reply via email
                  </Button>
                </a>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this submission?</DialogTitle>
            <DialogDescription>
              "{confirmDel?.subject}" from {confirmDel?.email} will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteSubmission(confirmDel.id)} data-testid="contact-confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const toneCls = {
    emerald: 'text-emerald-400',
    purple: 'text-purple-400',
    slate: 'text-slate-400',
  }[tone] || 'text-primary';
  return (
    <Card data-testid={testid}>
      <CardContent className="py-4 px-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
          <ChatCircle size={12} weight="duotone" /> {label}
        </div>
        <div className={`text-2xl font-bold ${toneCls}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
